import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { NavigationContext } from "navigation-react";
import mainLogo from "./images/mainlogo.png";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const DashboardContentHeader = () => {
  const { stateNavigator } = useContext(NavigationContext);
  const username = ls.get("username");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogout = async () => {
    // Clear any previous errors
    setError("");

    // Start loading state up-front
    setIsLoading(true);

    // Read the token inside the handler to avoid stale outer-scope references
    const token = ls.get("userlogout_token");

    // Early exit when there's no token (ensure loading is cleared)
    if (!token) {
      console.warn("⚠️ No logout token found, clearing local session");
      ls.clear();
      stateNavigator.navigate("login", { replace: true });
      setIsLoading(false);
      return;
    }

    let res;
    try {
      const logoutUrl = `${process.env.REACT_APP_BASE_URL}/user/logout?_format=json&token=${token}`;

      res = await fetch(logoutUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    } catch (err) {
      console.error("❌ Logout error:", err);
      setError(err.message || "Logout failed. Please try again.");
      alert(`Logout failed: ${err.message || "Please try again"}`);
      setIsLoading(false);
      return;
    }

    // Handle response outside the try/catch to avoid conditional/logical blocks inside try
    if (!res.ok && res.status !== 204) {
      const text = await res.text();
      alert(`Logout failed (status ${res.status}): ${text}`);
    }

    console.log("✅ Logout successful");

    // ONLY navigate and clear on SUCCESS
    ls.clear();
    stateNavigator.navigate("login", { replace: true });

    // Clear loading state
    setIsLoading(false);
  };

  const styles = {
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0 2rem",
      height: "70px",
      background: "#ffffff",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      position: "sticky",
      top: 0,
      zIndex: 1000,
      borderBottom: "1px solid #e8ecf1",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
    },
    logoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    logoImage: {
      height: "40px",
      width: "auto",
    },
    logoText: {
      color: "#2d3748",
      fontSize: "20px",
      fontWeight: 600,
      letterSpacing: "0.5px",
      borderLeft: "2px solid #e2e8f0",
      paddingLeft: "12px",
    },
    headerRight: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    userInfo: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "6px 14px",
      background: "#f7fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    },
    userAvatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      background: "#4299e1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      fontSize: "14px",
    },
    userName: {
      color: "#2d3748",
      fontSize: "14px",
      fontWeight: 500,
    },
    logoutBtn: {
      padding: "26px 20px",
      borderWidth: "medium",
      borderStyle: "none",
      borderColor: "currentcolor",
      borderImage: "initial",
      background: "rgb(252, 129, 129)",
      color: "rgb(255, 255, 255)",
      fontSize: "14px",
      fontWeight: 500,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "0.2s",
    },
    logoutBtnHover: {
      background: "#f56565",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(245, 101, 101, 0.3)",
    },
    logoutBtnDisabled: {
      opacity: 0.6,
      cursor: "not-allowed",
      transform: "none",
    },
    logoutBtnIcon: {
      fontSize: "14px",
    },
    errorMessage: {
      color: "#e53e3e",
      fontSize: "14px",
      marginRight: "10px",
      background: "#fed7d7",
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #feb2b2",
    },
  };

  const [isLogoutHovered, setIsLogoutHovered] = useState(false);

  // Responsive styles
  const responsiveStyles = `
    @media (max-width: 768px) {
      .pos-header-custom {
        padding: 0 1rem !important;
        height: 60px !important;
      }
      .logo-text-custom {
        font-size: 16px !important;
        padding-left: 8px !important;
      }
      .logo-image-custom {
        height: 32px !important;
      }
      .user-info-custom {
        padding: 4px 10px !important;
        gap: 8px !important;
      }
      .user-name-custom {
        font-size: 13px !important;
      }
      .logout-btn-custom {
        padding: 6px 14px !important;
        font-size: 13px !important;
      }
      .error-message-custom {
        font-size: 12px !important;
        padding: 4px 8px !important;
        margin-right: 6px !important;
      }
    }
    @media (max-width: 480px) {
      .logo-text-custom {
        display: none !important;
      }
      .user-name-custom {
        display: none !important;
      }
      .logout-btn-text {
        display: none !important;
      }
      .logout-btn-custom {
        padding: 8px 12px !important;
      }
      .error-message-custom {
        font-size: 11px !important;
        padding: 3px 6px !important;
        max-width: 100px !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <header className="pos-header-custom" style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoContainer}>
            <img
              src={mainLogo}
              alt="Reactive System Developers"
              className="logo-image-custom"
              style={styles.logoImage}
            />
          </div>
        </div>

        <div style={styles.headerRight}>
          {/* Show error if logout failed */}
          {error && (
            <div className="error-message-custom" style={styles.errorMessage}>
              ⚠️ {error}
            </div>
          )}

          <div className="user-info-custom" style={styles.userInfo}>
            <div style={styles.userAvatar}>
              <FontAwesomeIcon icon={faUser} />
            </div>
            <span className="user-name-custom" style={styles.userName}>
              {username || "Guest"}
            </span>
          </div>

          <button
            className="logout-btn-custom"
            style={{
              ...styles.logoutBtn,
              ...(isLogoutHovered && !isLoading ? styles.logoutBtnHover : {}),
              ...(isLoading ? styles.logoutBtnDisabled : {}),
            }}
            onClick={handleLogout}
            disabled={isLoading}
            onMouseEnter={() => setIsLogoutHovered(true)}
            onMouseLeave={() => setIsLogoutHovered(false)}
          >
            <FontAwesomeIcon icon={faSignOutAlt} style={styles.logoutBtnIcon} />
            <span className="logout-btn-text">
              {isLoading ? "Logging out..." : "Logout"}
            </span>
          </button>
        </div>
      </header>
    </>
  );
};

export default DashboardContentHeader;
